import type { ProjectNode } from '@/shared/types/projectData';
import { isDocumentDisplayRecord } from './documentRecordClassification';

export interface GanttGroupMoveClassification {
  descendantIds: string[];
  movableIds: string[];
  immutableAnchorIds: string[];
}

export const hasGanttGroupMoveSchedule = (node: Partial<ProjectNode>): boolean =>
  Boolean(node.startDate || node.endDate || node.dueDate || node.date);

export const isGanttGroupMoveImmutableAnchor = (node: Partial<ProjectNode>): boolean =>
  isDocumentDisplayRecord(node);

export const isGanttGroupMoveMovableNode = (node: Partial<ProjectNode>): boolean => {
  if (isGanttGroupMoveImmutableAnchor(node)) {
    return false;
  }

  if (node.type !== 'task' && node.type !== 'milestone') {
    return false;
  }

  return hasGanttGroupMoveSchedule(node);
};

export const classifyGanttGroupMoveDescendants = (
  nodes: ProjectNode[],
  groupId: string
): GanttGroupMoveClassification => {
  const descendantIds: string[] = [];
  const movableIds: string[] = [];
  const immutableAnchorIds: string[] = [];
  const visited = new Set<string>();

  const collect = (parentId: string): void => {
    if (visited.has(parentId)) {
      return;
    }
    visited.add(parentId);

    nodes.forEach((node) => {
      if (node.parentId !== parentId || node.id === parentId) {
        return;
      }

      descendantIds.push(node.id);

      if (isGanttGroupMoveImmutableAnchor(node)) {
        immutableAnchorIds.push(node.id);
      } else if (isGanttGroupMoveMovableNode(node)) {
        movableIds.push(node.id);
      }

      if (node.type === 'group') {
        collect(node.id);
      }
    });
  };

  collect(groupId);

  return {
    descendantIds,
    movableIds,
    immutableAnchorIds,
  };
};
